import React, { useState, useEffect } from 'react';

const PersonalInfoStep = ({ onNext, formData }) => {
    const [email, setEmail] = useState(formData.email || '');
    const [otp, setOtp] = useState('');
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [timer, setTimer] = useState(0); // Countdown timer

    const [name, setName] = useState(formData.name || '');
    const [countryCode, setCountryCode] = useState('');
    const [phone, setPhone] = useState(formData.phone || '');

    const [error, setError] = useState('');
    const [otpError, setOtpError] = useState('');
    const [loading, setLoading] = useState(false);

    const backendBaseUrl = 'https://api.karrentals.ca';

    // Start the timer whenever OTP is sent
    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const sendOtp = async () => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Invalid email format.');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${backendBaseUrl}/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setIsOtpSent(true);
                setTimer(30); // Start a 1-minute countdown
            } else {
                setError(data.error || 'Failed to send OTP. Please try again.');
            }
        } catch (err) {
            setError('An error occurred while sending OTP.');
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async () => {
        if (!otp) {
            setOtpError('OTP cannot be empty.');
            return;
        }

        setOtpError('');
        setLoading(true);

        try {
            const response = await fetch(`${backendBaseUrl}/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otp }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setIsEmailVerified(true);
                setOtpError('');
            } else {
                setOtpError(data.error || 'Invalid OTP.');
            }
        } catch (err) {
            setOtpError('An error occurred while verifying OTP.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => {
        if (!name.trim()) {
            setError('Name cannot be empty.');
            return;
        }

        if (!/^\d+$/.test(countryCode) || countryCode.length > 4) {
            setError('Country code must be numeric and 1-4 digits.');
            return;
        }

        if (!/^\d{10}$/.test(phone)) {
            setError('Phone number must be 10 digits.');
            return;
        }

        setError('');
        onNext({ name, email, phone: `+${countryCode}${phone}` });
    };

    return (
        <div>
            <h2 className="text-2xl font-bold mb-4">Personal Info</h2>
            <div className="space-y-4">
                {!isEmailVerified ? (
                    <>
                        {/* Email Input */}
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter Email address"
                            className="w-full border rounded px-4 py-2"
                            disabled={isOtpSent}
                        />
                        {isOtpSent && (
                            <>
                                <input
                                    type="text"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    placeholder="Enter OTP"
                                    className="w-full border rounded px-4 py-2"
                                />
                                <button
                                    onClick={verifyOtp}
                                    className="mt-2 px-4 py-2 bg-black text-white rounded"
                                    disabled={loading}
                                >
                                    {loading ? 'Verifying...' : 'Verify OTP'}
                                </button>
                                {otpError && <p className="text-red-500 text-sm">{otpError}</p>}

                                {/* Check Junk Mail Reminder */}
                                <p className="text-sm text-gray-600 mt-1">
                                    Didn’t receive the OTP? Please check your junk or spam folder.
                                </p>

                                <p className="text-sm mt-2">
                                    {timer > 0 ? (
                                        <span className="text-gray-500">
                                            Resend OTP in {timer}s
                                        </span>
                                    ) : (
                                        <button
                                            onClick={sendOtp}
                                            className="text-blue-500 underline"
                                            disabled={loading}
                                        >
                                            Send Again
                                        </button>
                                    )}
                                </p>
                            </>
                        )}
                        {!isOtpSent && (
                            <button
                                onClick={sendOtp}
                                className="mt-2 px-4 py-2 bg-black text-white rounded"
                                disabled={loading}
                            >
                                {loading ? 'Sending...' : 'Send OTP'}
                            </button>
                        )}
                        {error && <p className="text-red-500 text-sm">{error}</p>}
                    </>
                ) : (
                    <>
                        {/* Name Input */}
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter Renter's Name"
                            className="w-full border rounded px-4 py-2"
                        />

                        {/* Phone Input with Country Code */}
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center border rounded px-4 py-2 bg-gray-100">
                                <span className="text-lg font-bold pr-1">+</span>
                                <input
                                    type="text"
                                    value={countryCode}
                                    onChange={(e) => setCountryCode(e.target.value)}
                                    placeholder="Code"
                                    className="w-12 bg-transparent border-none outline-none"
                                />
                            </div>
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="Enter Phone Number"
                                className="flex-1 border rounded px-4 py-2"
                            />
                        </div>

                        {error && <p className="text-red-500 text-sm">{error}</p>}

                        <button
                            onClick={handleSubmit}
                            className="mt-6 px-4 py-2 bg-black text-white rounded"
                        >
                            Next
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default PersonalInfoStep;
